import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, LayoutGroup, motion, useAnimationControls } from "framer-motion";
import { LEARN, stickerButton } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import { fitTiles, seededShuffle, useCompleteOnce } from "./engineUtils";
import {
  Art,
  Counter,
  GhostSlot,
  IconCheck,
  PanelHeader,
  ROOT_STYLE,
  SparkleBurst,
  StarRow,
  StickerTile,
  Tray,
  choiceGrid,
  frost,
  letterWordArt,
  normalizeItems,
  tileMultForCount,
  tileSize,
  useArenaMetrics,
  useLetterAccent,
  useTileFeedback,
  withTimeout,
} from "./tapUtils";

/**
 * Config-driven tap games. Picks a mode from the config shape:
 *  - `choices` + `correctIndex`/`correctId`  → "Which one?" choice round
 *  - `items`/`options`/`hotspots`/`objects`  → "Tap all the …" collect round
 *  - `rounds[{ word, spokenParts }]`          → word-steps (syllable stones)
 *  - `interactionSteps` / `hasCompleteButton` → guided steps
 *  - thin (title/prompt only)                 → light up the letter (`requiredTaps`)
 */
export default function GenericTapGame({ letter, config = {}, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { accent, deep } = useLetterAccent(letter);
  const common = { letter, config, speak, sfx, finish, onWrong, reduceMotion, accent, deep };

  if (config.choices?.length && (config.correctIndex != null || config.correctId != null)) {
    return <ChoiceRound {...common} />;
  }
  const items = config.items || config.options || config.hotspots || config.objects;
  if (items?.length) return <CollectRound {...common} items={items} />;
  if (config.rounds?.length && config.rounds.some((r) => r?.word)) return <WordStepsRound {...common} />;
  if (config.hasCompleteButton || config.interactionSteps?.length) return <StepsRound {...common} />;
  return <LightUpLetter {...common} />;
}

/* ── Which one? ─────────────────────────────────────────────────────── */

function ChoiceRound({ letter, config, speak, sfx, finish, onWrong, reduceMotion, accent }) {
  const bp = useBreakpoint();
  const compact = bp === "xs" || bp === "sm";
  const choices = useMemo(
    () => config.choices.map((c, i) => {
      const label = typeof c === "string" ? c : c.label || c.word || c.id || "";
      const id = typeof c === "object" ? c.id || label : label;
      const ok = config.correctIndex === i || (config.correctId != null && config.correctId === id);
      return { key: `${label}-${i}`, label, id, ok, assetName: typeof c === "object" ? c.assetName : null };
    }),
    [config.choices, config.correctIndex, config.correctId],
  );
  const { shakes, pulses, shakeId, pulseId } = useTileFeedback();
  const [solved, setSolved] = useState(null);
  const [burst, setBurst] = useState(0);

  useEffect(() => {
    if (!solved) return undefined;
    const t = setTimeout(finish, reduceMotion ? 250 : 900);
    return () => clearTimeout(t);
  }, [solved, finish, reduceMotion]);

  const tap = (c) => {
    if (solved) return;
    if (!c.ok) { shakeId(c.key); onWrong?.(); return; }
    sfx?.("correct");
    speak?.(c.label);
    pulseId(c.key);
    setBurst((b) => b + 1);
    setSolved(c.key);
  };

  const mult = tileMultForCount(choices.length);
  return (
    <div style={ROOT_STYLE}>
      <ConfettiBurst key={burst} active={burst > 0} originY={45} count={28} reduceMotion={reduceMotion} />
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 8 }}>
        <div style={choiceGrid(choices.length, compact)}>
          {choices.map((c, i) => (
            <div key={c.key} style={{ position: "relative" }}>
              <StickerTile
                letter={letter}
                assetName={c.assetName}
                word={c.label}
                label={c.label}
                size={mult}
                index={i}
                accent={accent}
                reduceMotion={reduceMotion}
                shake={shakes[c.key] || 0}
                pulse={pulses[c.key] || 0}
                state={solved ? (solved === c.key ? "correct" : "dim") : "idle"}
                disabled={!!solved}
                onTap={() => tap(c)}
              />
              {solved === c.key && <SparkleBurst trigger={pulses[c.key] || 0} color={accent} reduceMotion={reduceMotion} />}
            </div>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {solved && (
          <motion.div
            initial={reduceMotion ? false : { y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            style={frost({ margin: "0 auto 4px", padding: "10px 18px", display: "flex", alignItems: "center", gap: 10, fontSize: 22, fontWeight: 700 })}
          >
            <span style={{ width: 30, height: 30, borderRadius: "50%", background: LEARN.color.mint, display: "grid", placeItems: "center" }}><IconCheck size={18} /></span>
            {choices.find((c) => c.key === solved)?.label}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Tap all the … (collect into the tray) ──────────────────────────── */

function CollectRound({ letter, config, items, speak, sfx, finish, onWrong, reduceMotion, accent, deep }) {
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const round = useMemo(() => {
    const all = normalizeItems(items, letter);
    return seededShuffle(all, 17).slice(0, config.roundSize || all.length);
  }, [items, letter, config.roundSize]);
  const need = round.filter((x) => x.isGood).length;
  const [collected, setCollected] = useState([]); // ordered keys
  const [popping, setPopping] = useState(null);
  const { shakes, pulses, shakeId, pulseId } = useTileFeedback();
  const [burst, setBurst] = useState(0);
  const timers = useRef([]);
  const rootRef = useRef(null);
  const shelfRef = useRef(null);
  const { w, h, tile } = useArenaMetrics(rootRef);
  const shelf = useArenaMetrics(shelfRef);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const done = need > 0 && collected.length >= need;
  useEffect(() => {
    if (!done) return undefined;
    sfx?.("sparkle");
    setBurst(1);
    const t = setTimeout(finish, reduceMotion ? 250 : 800);
    return () => clearTimeout(t);
  }, [done, finish, reduceMotion, sfx]);

  const tap = (it) => {
    if (done || collected.includes(it.key) || popping === it.key) return;
    if (!it.isGood) { shakeId(it.key); onWrong?.(); return; }
    sfx?.("pop");
    speak?.(it.word);
    pulseId(it.key);
    setPopping(it.key);
    const t = setTimeout(() => {
      setPopping((p) => (p === it.key ? null : p));
      setCollected((c) => (c.includes(it.key) ? c : [...c, it.key]));
    }, reduceMotion ? 0 : 340);
    timers.current.push(t);
  };

  // Pieces start at 1.15 tiles (small banks get the roomier count-based size)
  // and shrink to 0.85 when a big bank needs a fourth row on a laptop arena,
  // so the grid and the shelf always share the arena without scrolling.
  const slot = need > 8 ? 0.5 : need > 6 ? 0.6 : 0.72;
  const byKey = useMemo(() => new Map(round.map((x) => [x.key, x])), [round]);
  const trayH = shelf.h || (tile ? 44 + 40 + tile * slot : 0);
  const fit = fitTiles({
    count: round.length,
    w: w ? w - 20 : 0,
    h: h ? h - trayH - tile * 0.22 - 16 : 0,
    tile,
    max: Math.max(1.15, tileMultForCount(round.length)),
    min: 0.85,
    gap: 0.16,
  });
  const mult = fit.scale;

  return (
    <LayoutGroup id={`collect-${letter}`}>
      <div ref={rootRef} style={ROOT_STYLE}>
        {/* Grid + tray travel together as one block, centred in the arena
            (margin:auto); the fit above keeps them inside it. */}
        <div style={{ flex: 1, minHeight: 0, overflowY: fit.fits ? "hidden" : "auto", overflowX: "hidden", display: "flex", flexDirection: "column", padding: "8px 10px" }}>
        <div style={{ margin: "auto", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "calc(var(--tile, 72px) * 0.22)" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${fit.cols}, max-content)`,
              justifyContent: "center",
              gap: "calc(var(--tile, 72px) * 0.16)",
              width: "100%",
            }}
          >
            {round.map((it, i) => {
              const isCollected = collected.includes(it.key);
              return (
                <div key={it.key} style={{ position: "relative", display: "flex", justifyContent: "center" }}>
                  {isCollected ? (
                    <GhostSlot size={mult} accent={accent} />
                  ) : (
                    <StickerTile
                      layoutId={`tile-${it.key}`}
                      letter={letter}
                      assetName={it.assetName}
                      word={it.word}
                      size={mult}
                      index={i}
                      accent={accent}
                      reduceMotion={reduceMotion}
                      shake={shakes[it.key] || 0}
                      pulse={pulses[it.key] || 0}
                      state={popping === it.key ? "correct" : "idle"}
                      onTap={() => tap(it)}
                    />
                  )}
                  {popping === it.key && <SparkleBurst trigger={pulses[it.key] || 0} color={accent} reduceMotion={reduceMotion} />}
                </div>
              );
            })}
          </div>
        <div ref={shelfRef} style={{ position: "relative", flexShrink: 0, width: "min(100%, 640px)", boxSizing: "border-box" }}>
          <ConfettiBurst key={burst} active={burst > 0} originY={20} count={30} spread={90} reduceMotion={reduceMotion} />
          <Tray total={need} accent={accent} deep={deep} title="Collected words" slotSize={slot} compact={compact}>
            {collected.map((k) => {
              const it = byKey.get(k);
              return (
                <motion.span
                  key={k}
                  layoutId={`tile-${k}`}
                  transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 340, damping: 28 }}
                  style={{ display: "inline-flex", width: tileSize(slot), height: tileSize(slot), borderRadius: `calc(var(--tile, 72px) * ${slot} * 0.24)`, background: "linear-gradient(180deg, #FFFFFF 0%, #F6F8FC 100%)", border: `2px solid ${accent}55`, alignItems: "center", justifyContent: "center", boxShadow: `${LEARN.shadow.card}, inset 0 1px 0 #fff`, flexShrink: 0, boxSizing: "border-box" }}
                  aria-label={it?.word}
                >
                  <Art letter={letter} assetName={it?.assetName} word={it?.word} alt="" />
                </motion.span>
              );
            })}
          </Tray>
        </div>
        </div>
        </div>
      </div>
    </LayoutGroup>
  );
}

/* ── Word steps (syllable stones) ───────────────────────────────────── */

function WordStepsRound({ letter, config, speak, sfx, finish, onWrong, reduceMotion, accent, deep }) {
  const rounds = useMemo(
    () => (config.rounds || []).filter((r) => r?.word).map((r) => ({
      word: String(r.word),
      parts: (r.spokenParts?.length ? r.spokenParts : [r.word]).map(String),
      art: r.assetName || letterWordArt(letter, r.word),
    })),
    [config.rounds, letter],
  );
  const [ri, setRi] = useState(0);
  const [step, setStep] = useState(0);
  const [solved, setSolved] = useState(false);
  const { shakes, pulses, shakeId, pulseId } = useTileFeedback();
  const [burst, setBurst] = useState(0);
  const round = rounds[ri];

  useEffect(() => {
    if (!solved || !round) return undefined;
    const t = setTimeout(() => {
      if (ri + 1 >= rounds.length) { finish(); return; }
      setRi((x) => x + 1);
      setStep(0);
      setSolved(false);
    }, reduceMotion ? 300 : 1100);
    return () => clearTimeout(t);
  }, [solved, ri, rounds.length, finish, reduceMotion, round]);

  useEffect(() => { if (!rounds.length) finish(); }, [rounds.length, finish]);
  if (!round) return <div style={ROOT_STYLE} />;

  const tap = async (i) => {
    if (solved) return;
    const id = `${ri}-${i}`;
    if (i !== step) { shakeId(id); onWrong?.(); return; }
    sfx?.("pop");
    pulseId(id);
    const next = step + 1;
    setStep(next);
    if (next >= round.parts.length) {
      setSolved(true);
      setBurst((b) => b + 1);
      await withTimeout(speak?.(round.parts[i]), 1500);
      sfx?.("correct");
      speak?.(round.word);
    } else {
      speak?.(round.parts[i]);
    }
  };

  return (
    <div style={ROOT_STYLE}>
      <ConfettiBurst key={burst} active={burst > 0} originY={35} count={26} reduceMotion={reduceMotion} />
      <Counter done={ri + (solved ? 1 : 0)} total={rounds.length} accent={accent} />
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 6 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={ri}
            initial={reduceMotion ? false : { x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={reduceMotion ? undefined : { x: -40, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={frost({ display: "flex", flexDirection: "column", alignItems: "stretch", width: "min(100%, 560px)", boxSizing: "border-box", overflow: "hidden" })}
          >
            <PanelHeader accent={accent} deep={deep} badge={letter} title="Tap the parts in order" />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "14px 16px 18px" }}>
            {round.art && (
              <span style={{ width: tileSize(1.9), height: tileSize(1.9), display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Art letter={letter} assetName={round.art} word={round.word} alt="" style={{ width: "100%", height: "100%" }} />
              </span>
            )}
            <motion.div
              animate={solved && !reduceMotion ? { scale: [1, 1.12, 1] } : { scale: 1 }}
              transition={{ duration: 0.5 }}
              style={{ fontSize: "clamp(28px, calc(var(--tile, 72px) * 0.5), 46px)", fontWeight: 700, color: solved ? accent : LEARN.color.ink, letterSpacing: "0.01em", lineHeight: 1.1 }}
            >
              {round.word}
            </motion.div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "calc(var(--tile, 72px) * 0.14)", justifyContent: "center", alignItems: "flex-start", marginTop: 4 }}>
              {round.parts.map((p, i) => {
                const id = `${ri}-${i}`;
                const state = i < step ? "done" : i === step && !solved ? "idle" : i === step ? "done" : "idle";
                return (
                  <div key={id} style={{ position: "relative" }}>
                    <StickerTile
                      size={1.15}
                      index={i}
                      accent={accent}
                      reduceMotion={reduceMotion}
                      shake={shakes[id] || 0}
                      pulse={pulses[id] || 0}
                      state={state}
                      ariaLabel={`Part ${i + 1}: ${p}`}
                      onTap={() => tap(i)}
                      style={{ background: i === step && !solved ? `${accent}14` : undefined, boxShadow: i === step && !solved ? `${LEARN.shadow.card}, 0 0 0 3px ${accent}` : undefined }}
                    >
                      <span style={{ fontSize: "clamp(18px, calc(var(--tile, 72px) * 0.32), 30px)", fontWeight: 700, color: i < step || solved ? deep : LEARN.color.ink }}>{p}</span>
                    </StickerTile>
                    <SparkleBurst trigger={pulses[id] || 0} color={accent} reduceMotion={reduceMotion} />
                  </div>
                );
              })}
            </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ── Guided steps ───────────────────────────────────────────────────── */

function StepsRound({ config, speak, sfx, finish, letter, reduceMotion, accent, deep }) {
  const steps = useMemo(
    () => (config.interactionSteps?.length ? config.interactionSteps : [{ label: config.completeLabel || "Done" }]),
    [config.interactionSteps, config.completeLabel],
  );
  const [si, setSi] = useState(0);
  const step = steps[Math.min(si, steps.length - 1)];
  const [pulse, setPulse] = useState(0);

  const next = () => {
    sfx?.("pop");
    speak?.(step?.label || letter);
    setPulse((p) => p + 1);
    if (si + 1 >= steps.length) { sfx?.("correct"); finish(); } else setSi((x) => x + 1);
  };

  return (
    <div style={ROOT_STYLE}>
      {steps.length > 1 && <Counter done={si} total={steps.length} accent={accent} />}
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 8 }}>
        <div style={frost({ padding: "22px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 18, width: "min(100%, 480px)", boxSizing: "border-box", textAlign: "center" })}>
          {(step?.prompt || step?.text) && <p style={{ margin: 0, fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>{step.prompt || step.text}</p>}
          <motion.button
            type="button"
            onClick={next}
            animate={pulse && !reduceMotion ? { scale: [1, 1.08, 1] } : { scale: 1 }}
            whileTap={reduceMotion ? undefined : { scale: 0.96, y: 2 }}
            style={{ ...stickerButton({ bg: accent, deep, size: "lg" }), minWidth: 200 }}
          >
            {step?.label || config.completeLabel || "Continue"}
          </motion.button>
        </div>
      </div>
    </div>
  );
}

/* ── Light up the letter ────────────────────────────────────────────── */

function LightUpLetter({ letter, config, speak, sfx, finish, reduceMotion, accent, deep }) {
  const need = Math.max(1, Number(config.requiredTaps) || 3);
  const [count, setCount] = useState(0);
  const lit = count >= need;
  const [burst, setBurst] = useState(0);

  useEffect(() => {
    if (!lit) return undefined;
    sfx?.("sparkle");
    setBurst(1);
    const t = setTimeout(finish, reduceMotion ? 300 : 900);
    return () => clearTimeout(t);
  }, [lit, finish, reduceMotion, sfx]);

  const glyph = useAnimationControls();
  useEffect(() => {
    if (!count || reduceMotion) return;
    glyph.start(lit ? { scale: [1, 1.2, 1.06], transition: { duration: 0.5, ease: "easeOut" } } : { scale: [1, 1.14, 1], transition: { duration: 0.42, ease: "easeOut" } });
  }, [count, lit, reduceMotion, glyph]);

  const tap = useCallback(() => {
    if (lit) return;
    sfx?.("pop", { rate: 1 + count * 0.08 });
    speak?.(letter);
    setCount((c) => c + 1);
  }, [lit, sfx, speak, letter, count]);

  const glow = lit ? `0 0 24px ${accent}99, 0 0 60px ${accent}55` : "none";
  return (
    <div style={ROOT_STYLE}>
      <ConfettiBurst key={burst} active={burst > 0} originY={40} count={32} reduceMotion={reduceMotion} />
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 8 }}>
        <div style={frost({ padding: "16px 20px 18px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, width: "min(100%, 460px)", boxSizing: "border-box" })}>
          <StarRow total={need} done={count} />
          <motion.button
            type="button"
            onClick={tap}
            aria-label={`Tap the letter ${letter}`}
            aria-pressed={lit}
            whileTap={!lit && !reduceMotion ? { scale: 0.92 } : undefined}
            animate={glyph}
            style={{
              position: "relative",
              width: "min(calc(var(--arena-w, 400px) * 0.62), calc(var(--arena-h, 400px) * 0.48), 300px)",
              aspectRatio: "1 / 1",
              borderRadius: "28%",
              border: "none",
              background: lit ? `linear-gradient(160deg, ${accent} 0%, ${deep} 100%)` : "#fff",
              boxShadow: lit ? `${LEARN.shadow.sticker(deep)}, ${glow}` : `${LEARN.shadow.card}, inset 0 0 0 3px ${accent}33`,
              cursor: lit ? "default" : "pointer",
              display: "grid",
              placeItems: "center",
              WebkitTapHighlightColor: "transparent",
              fontFamily: LEARN.font,
              fontWeight: 700,
              fontSize: "min(calc(var(--arena-w, 400px) * 0.4), calc(var(--arena-h, 400px) * 0.32), 200px)",
              lineHeight: 1,
              color: lit ? "#fff" : accent,
              textShadow: lit ? "0 4px 0 rgba(11,26,51,0.18)" : "none",
              transition: reduceMotion ? "none" : "background 260ms, color 260ms, box-shadow 320ms",
            }}
          >
            {letter}
            <SparkleBurst trigger={count} color={LEARN.color.sun} reduceMotion={reduceMotion} distance={90} count={10} />
          </motion.button>
          <div style={{ fontSize: 17, fontWeight: 700, color: lit ? deep : LEARN.color.muted, transition: "color 200ms" }}>
            {lit ? "All lit up!" : `${count} of ${need} taps`}
          </div>
        </div>
      </div>
    </div>
  );
}
